import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { RefreshCw, Star, CalendarClock, Archive, Info } from 'lucide-react';
import { backofficeApi, formatNumber, formatDate, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type {
  BackofficeFeatureRowDto,
  ComboItem,
  FeatureOverviewQuery,
  MerchantFeatureLifecycle,
  MerchantFeatureScope,
  MerchantPartnershipStatus,
  Tone,
} from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  ConfirmModal,
  Select,
  SearchableComboBox,
  TextInput,
  Pagination,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
  align-items: flex-end;

  /* O combobox põe a borda no wrapper e o select nele mesmo; sem isto a linha
     de filtros fica escalonada. */
  select {
    box-sizing: border-box;
    height: 42px;
  }
`;

const FilterField = styled.div`
  flex: 1 1 190px;
  min-width: 160px;
`;

const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;

  strong {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }
  small {
    font-size: 0.75rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const Reason = styled.span`
  display: block;
  max-width: 320px;
  font-size: 0.82rem;
  color: ${(p) => p.theme.textMuted};
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const Note = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 1rem 0 0;
  font-size: 0.8rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textMuted};

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const SCOPE_LABELS: Record<MerchantFeatureScope, string> = {
  PLATFORM: 'Plataforma inteira',
  LOCALITY: 'Uma cidade',
  CATEGORY: 'Uma categoria',
};

const LIFECYCLE_LABELS: Record<MerchantFeatureLifecycle, string> = {
  ACTIVE: 'Vigente',
  SCHEDULED: 'Agendado',
  ENDED: 'Encerrado',
};

const LIFECYCLE_TONES: Record<MerchantFeatureLifecycle, Tone> = {
  ACTIVE: 'success',
  SCHEDULED: 'info',
  ENDED: 'secondary',
};

const PARTNERSHIP_LABELS: Record<MerchantPartnershipStatus, string> = {
  NONE: 'Sem parceria',
  PENDING: 'Em negociação',
  ACTIVE: 'Parceira',
  SUSPENDED: 'Suspensa',
  EXPIRED: 'Expirada',
  TERMINATED: 'Encerrada',
};

const SCOPE_OPTIONS = [
  { value: '', label: 'Todos os escopos' },
  { value: 'PLATFORM', label: SCOPE_LABELS.PLATFORM },
  { value: 'LOCALITY', label: SCOPE_LABELS.LOCALITY },
  { value: 'CATEGORY', label: SCOPE_LABELS.CATEGORY },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Todas as vigências' },
  { value: 'ACTIVE', label: 'Vigentes' },
  { value: 'SCHEDULED', label: 'Agendados' },
  { value: 'ENDED', label: 'Encerrados' },
];

/** O contexto de um destaque, por extenso. É a coluna que a tela existe para ter. */
function contextOf(row: BackofficeFeatureRowDto): string {
  if (row.scope === 'LOCALITY') return `Cidade · ${row.localityName ?? '-'}`;
  if (row.scope === 'CATEGORY') return `Categoria · ${row.categoryName ?? '-'}`;
  return 'Plataforma inteira';
}

interface Props {
  permissions: string[];
  isAdmin: boolean;
}

/**
 * A tela Destaques - a visão que atravessa as empresas (card 262).
 *
 * ## Que pergunta ela responde
 *
 * O drawer do lojista responde *"esta empresa está em destaque?"*. Esta
 * responde a inversa, **"o que está em destaque em Toledo agora?"**, que é a
 * pergunta de quem opera e a única que não dá para responder abrindo empresa
 * por empresa.
 *
 * ## Por que os contadores não saem da lista
 *
 * Somar `content` daria o total da página, não do recorte - "3 vigentes" seria
 * a resposta certa para uma pergunta que ninguém fez. Os cartões vêm de uma
 * chamada própria, sobre o recorte inteiro, e por isso paginar não os
 * recarrega. Eles também ignoram o filtro de vigência de propósito: um contador
 * de agendados que zerasse ao filtrar "vigentes" deixaria de contar e passaria
 * a ecoar o filtro.
 *
 * ## Criar continua sendo no drawer
 *
 * Aqui só se lê e se encerra. Criar exige parceria ativa, motivo e contexto, e
 * ter duas portas para a mesma escrita seria duas chances de esquecer o motivo
 * - que é o que responde, seis meses depois, por que aquela empresa estava no
 * topo.
 */
const FeaturesPage: React.FC<Props> = ({ permissions, isAdmin }) => {
  const { showNotification } = useNotification();
  const canManage = isAdmin || permissions.includes('ROLE_BACKOFFICE_PARTNERSHIP_MANAGE');

  const [scope, setScope] = useState<string>('');
  const [localityId, setLocalityId] = useState<string>('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [ending, setEnding] = useState<BackofficeFeatureRowDto | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  /**
   * O recorte sem a vigência. É o que os contadores usam, e é também a metade
   * estável do filtro da lista - separá-lo evita recarregar os cartões quando
   * só a vigência muda.
   */
  const context: Omit<FeatureOverviewQuery, 'status'> = useMemo(
    () => ({
      ...(scope ? { scope: scope as MerchantFeatureScope } : {}),
      ...(localityId ? { localityId } : {}),
      ...(categoryId ? { categoryId } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    }),
    [scope, localityId, categoryId, from, to]
  );

  const summary = useAsync(() => backofficeApi.getFeatureSummary(context), [context]);

  const list = useAsync(
    () =>
      backofficeApi.listFeatures({
        ...context,
        ...(status ? { status: status as MerchantFeatureLifecycle } : {}),
        page,
        size: pageSize,
      }),
    [context, status, page, pageSize]
  );

  // As duas listas de contexto são carregadas uma vez: são catálogos pequenos e
  // servem só para traduzir id em nome nos filtros.
  const localities = useAsync(() => backofficeApi.listLocalities({ size: 200, active: true }), []);
  const categories = useAsync(() => backofficeApi.listCategories({ size: 200 }), []);

  const localityItems: ComboItem[] = useMemo(
    () =>
      (localities.data?.content ?? []).map((l) => ({ id: l.id, label: `${l.name} - ${l.state}` })),
    [localities.data]
  );

  const categoryItems: ComboItem[] = useMemo(
    () => (categories.data?.content ?? []).map((c) => ({ id: c.id, label: c.name })),
    [categories.data]
  );

  /**
   * Qualquer troca de filtro volta para a primeira página.
   *
   * Sem isto, filtrar estando na página 4 mostra uma lista vazia que parece
   * "não há destaque nenhum" - e o operador conclui o oposto do que o dado diz.
   */
  const changeFilter = (apply: () => void): void => {
    apply();
    setPage(0);
  };

  const reload = (): void => {
    summary.reload();
    list.reload();
  };

  const confirmEnd = async (): Promise<void> => {
    if (!ending) return;
    setSaving(true);
    try {
      // Reusa o endpoint da empresa: encerrar continua sendo a mesma escrita
      // auditada do drawer, e o `merchantId` da linha é o que garante que o
      // destaque encerrado é o desta empresa.
      await backofficeApi.endMerchantFeature(ending.merchantId, ending.id);
      showNotification('Destaque encerrado.', 'success');
      setEnding(null);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível encerrar o destaque.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const rows = list.data?.content ?? [];
  const totalItems = list.data?.totalElements ?? 0;
  const totalPages = list.data?.totalPages ?? 0;

  return (
    <>
      <PageHeader
        title="Destaques"
        subtitle="O que está em destaque, em que contexto e até quando. Criar e encerrar destaque de uma empresa continua no cadastro dela."
      />

      <StatsGrid>
        <StatCard
          label="Vigentes (no recorte)"
          value={formatNumber(summary.data?.active ?? 0)}
          icon={<Star size={18} />}
        />
        <StatCard
          label="Agendados (no recorte)"
          value={formatNumber(summary.data?.scheduled ?? 0)}
          icon={<CalendarClock size={18} />}
        />
        <StatCard
          label="Encerrados (no recorte)"
          value={formatNumber(summary.data?.ended ?? 0)}
          icon={<Archive size={18} />}
        />
      </StatsGrid>

      <Toolbar>
        <FilterField>
          <SearchableComboBox
            label="Cidade"
            items={localityItems}
            valueId={localityId}
            onChange={(id) => changeFilter(() => setLocalityId(id))}
            placeholder="Todas as cidades"
            loading={localities.loading}
            noResultsText="Nenhuma cidade encontrada"
          />
        </FilterField>
        <FilterField>
          <SearchableComboBox
            label="Categoria"
            items={categoryItems}
            valueId={categoryId}
            onChange={(id) => changeFilter(() => setCategoryId(id))}
            placeholder="Todas as categorias"
            loading={categories.loading}
            noResultsText="Nenhuma categoria encontrada"
          />
        </FilterField>
        <FilterField>
          <Select
            label="Escopo"
            options={SCOPE_OPTIONS}
            value={scope}
            onChange={(e) => changeFilter(() => setScope(e.target.value))}
          />
        </FilterField>
        <FilterField>
          <Select
            label="Vigência"
            options={STATUS_OPTIONS}
            value={status}
            onChange={(e) => changeFilter(() => setStatus(e.target.value))}
          />
        </FilterField>
        <FilterField>
          <TextInput
            label="De"
            type="date"
            value={from}
            onChange={(e) => changeFilter(() => setFrom(e.target.value))}
          />
        </FilterField>
        <FilterField>
          <TextInput
            label="Até"
            type="date"
            value={to}
            onChange={(e) => changeFilter(() => setTo(e.target.value))}
          />
        </FilterField>
        <ActionButton title="Atualizar" onClick={reload}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={list.loading}
        error={list.error}
        isEmpty={rows.length === 0}
        emptyTitle="Nenhum destaque neste recorte"
        emptyDescription="Ajuste os filtros, ou crie um destaque pelo cadastro da empresa - destaque exige parceria ativa."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Contexto</th>
                <th style={{ whiteSpace: 'nowrap' }}>Período</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Vigência</th>
                <th>Motivo</th>
                {canManage && <th style={{ width: '1%' }} />}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <NameCell>
                      <strong>{row.merchantName}</strong>
                      <small>
                        {row.merchantCity ?? 'Sem cidade'}
                        {/* Destaque vigente de empresa não-parceira é anomalia:
                            o core encerra os destaques quando a parceria cai, e
                            ver um aqui é sinal de correção manual no banco. É
                            melhor que apareça nesta tela do que na vitrine. */}
                        {row.partnershipStatus !== 'ACTIVE' && (
                          <>
                            {' · '}
                            <Badge tone="warning">{PARTNERSHIP_LABELS[row.partnershipStatus]}</Badge>
                          </>
                        )}
                      </small>
                    </NameCell>
                  </td>
                  <td>{contextOf(row)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {formatDate(row.startsAt)} - {row.endsAt ? formatDate(row.endsAt) : 'sem fim'}
                  </td>
                  <td>
                    <Badge tone={LIFECYCLE_TONES[row.lifecycle]}>
                      {LIFECYCLE_LABELS[row.lifecycle]}
                    </Badge>
                  </td>
                  <td>
                    <Reason>{row.reason}</Reason>
                  </td>
                  {canManage && (
                    <td>
                      <RowActions>
                        {row.lifecycle !== 'ENDED' && (
                          <Button variant="ghost" onClick={() => setEnding(row)}>
                            Encerrar
                          </Button>
                        )}
                      </RowActions>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {totalPages > 1 && (
        <Pagination
          currentPage={page + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disabled={list.loading}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={(size) => changeFilter(() => setPageSize(size))}
        />
      )}

      <Note>
        <Info size={15} />
        <span>
          O motor de posicionamento tem teto: no máximo um promovido a cada cinco posições da
          vitrine, contando destaque e anúncio juntos. Acima disso o destaque continua gravado, mas
          a empresa não sobe - exposição vendida e não entregue. O aviso na criação, no cadastro da
          empresa, mostra quantos já disputam aquela vitrine antes de você prometer mais um.
        </span>
      </Note>

      <ConfirmModal
        isOpen={ending !== null}
        danger
        title="Encerrar destaque"
        message={
          <>
            {ending?.merchantName} sai das posições de destaque agora. O período já cumprido fica no
            registro - nada é apagado.
          </>
        }
        confirmLabel="Encerrar"
        loading={saving}
        onConfirm={confirmEnd}
        onClose={() => setEnding(null)}
      />
    </>
  );
};

export default FeaturesPage;
